import {useMemo, useState} from 'react';
import {PhoneHQAvatar, PhoneHQLoader, PhoneHQButton, PhoneHQCheckbox, PhoneHQGlyph} from 'ch3-ui-lib';
import {Search, X} from "lucide-react";

export interface SelectableMember<D = unknown> {
    id: string | number;
    name: string;
    subtitle?: string;
    avatarUrl?: string | null;
    disabled?: boolean;
    data: D;
}

interface SelectMembersDialogProps<D> {
    /** Members available to pick from (e.g. current search results). */
    members: SelectableMember<D>[];
    /** Members that are already selected, kept as full objects so chips persist across searches. */
    initialSelected: SelectableMember<D>[];
    /** Controlled search value — search itself is owned by the parent (often server-side). */
    searchValue: string;
    onSearchChange: (value: string) => void;
    onConfirm: (selected: SelectableMember<D>[]) => void;
    onClose: () => void;
    title?: string;
    confirmLabel?: string;
    searchPlaceholder?: string;
    emptyLabel?: string;
    loading?: boolean;
    showSelectAll?: boolean;
}

export function SelectMembersDialog<D>({
    members,
    initialSelected,
    searchValue,
    onSearchChange,
    onConfirm,
    onClose,
    title = 'Add members',
    confirmLabel = 'Add members',
    searchPlaceholder = 'Search name',
    emptyLabel = 'No users found',
    loading = false,
    showSelectAll = false,
}: SelectMembersDialogProps<D>) {
    const [selected, setSelected] = useState<SelectableMember<D>[]>(initialSelected);

    const selectedIds = useMemo(() => new Set(selected.map((m) => m.id)), [selected]);

    const toggle = (member: SelectableMember<D>) => {
        if (member.disabled) return;
        setSelected((prev) =>
            prev.some((m) => m.id === member.id)
                ? prev.filter((m) => m.id !== member.id)
                : [...prev, member]
        );
    };

    const selectAll = () => {
        setSelected((prev) => {
            const merged = [...prev];
            members.forEach((m) => {
                if (!m.disabled && !merged.some((x) => x.id === m.id)) merged.push(m);
            });
            return merged;
        });
    };

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="relative mx-4 max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-xl bg-black p-1 shadow-lg">
                <div className="flex items-center justify-between p-5">
                    <h2 className="text-base font-semibold text-white">{title}</h2>
                    <div className="flex items-center gap-2">
                        {showSelectAll && (
                            <button
                                onClick={selectAll}
                                className="rounded-lg px-3 py-2 text-white outline outline-white/[0.08] transition-colors"
                            >
                                Select all
                            </button>
                        )}
                        <X
                            onClick={onClose}
                            size={18}
                            className="cursor-pointer rounded-md text-white hover:bg-white/[0.12]"
                        />
                    </div>
                </div>

                <div className="overflow-y-auto" style={{maxHeight: 'calc(90vh - 140px)', paddingBottom: '80px'}}>
                    {/* Selected members */}
                    <div className={`flex flex-col ${selected.length > 0 ? 'border-b border-white/[0.08]' : ''}`}>
                        <div className="px-5 py-[6px] text-tiny text-zinc-500">
                            Members {selected.length > 0 && `(${selected.length})`}
                        </div>
                        {selected.length > 0 && (
                            <div className="flex gap-2 overflow-x-auto p-2">
                                {selected.map((member) => (
                                    <div key={member.id} className="flex min-w-0 flex-col items-center rounded-lg px-3 py-1">
                                        <div className="relative">
                                            <PhoneHQAvatar src={member.avatarUrl ?? undefined} name={member.name} size="40px"/>
                                            <button
                                                onClick={() => toggle(member)}
                                                className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-md bg-surface text-ink"
                                            >
                                                <PhoneHQGlyph name="close" size={14}/>
                                            </button>
                                        </div>
                                        <span className="mt-2 max-w-[64px] truncate text-center text-sm font-medium text-white">
                                            {member.name}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Search */}
                    <div className="relative">
                        <input
                            type="text"
                            value={searchValue}
                            onChange={(e) => onSearchChange(e.target.value)}
                            placeholder={searchPlaceholder}
                            className="w-full border-y border-white/[0.08] bg-black px-4 py-4 pl-[52px] text-white placeholder:text-zinc-500 focus:ring-0"
                        />
                        <Search size={18} className="absolute left-[20px] top-5 text-zinc-500"/>
                    </div>

                    {/* Members list */}
                    <div className="max-h-64 rounded-lg border-t border-white/[0.08] py-4">
                        {loading ? (
                            <div className="flex items-center justify-center py-6">
                                <PhoneHQLoader/>
                            </div>
                        ) : members.length === 0 ? (
                            <div className="p-4 text-center text-zinc-500">{emptyLabel}</div>
                        ) : (
                            <div className="pb-12">
                                {members.map((member) => {
                                    const checked = selectedIds.has(member.id);
                                    return (
                                        <div
                                            key={member.id}
                                            onClick={() => toggle(member)}
                                            className={`flex justify-between rounded-lg p-4 transition-colors ${
                                                member.disabled
                                                    ? 'cursor-not-allowed opacity-50'
                                                    : 'cursor-pointer hover:bg-white/[0.08]'
                                            }`}
                                        >
                                            <div className="flex flex-1 items-center space-x-3">
                                                <PhoneHQAvatar src={member.avatarUrl ?? undefined} name={member.name} size="40px"/>
                                                <div className="min-w-0">
                                                    <p className="truncate text-sm font-medium text-white">{member.name}</p>
                                                    {member.subtitle && (
                                                        <p className="truncate text-tiny text-zinc-400">{member.subtitle}</p>
                                                    )}
                                                </div>
                                            </div>
                                            {!member.disabled && (
                                                <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                                                    <PhoneHQCheckbox
                                                        theme="dark"
                                                        checked={checked}
                                                        onCheckedChange={() => toggle(member)}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                {/* Floating confirm button */}
                <div className="absolute bottom-0 left-0 right-0 flex p-4">
                    <PhoneHQButton
                        variant="primary"
                        className="mx-auto w-full max-w-xl"
                        onClick={() => {
                            onConfirm(selected);
                            onClose();
                        }}
                    >
                        {confirmLabel}
                    </PhoneHQButton>
                </div>
            </div>
        </div>
    );
}
